import React, { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Container,
  ProgressBar,
  ListGroup,
} from 'react-bootstrap';
import {
  useGetTransfersQuery,
  useDeleteTransferItemsMutation,
  useFixMissingProductIdsMutation,
} from '../../slices/transfersApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';

const TransfersSearchDeleteScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [previewResults, setPreviewResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState(0);

  const { data: transfersData, isLoading, error } = useGetTransfersQuery();
  const [deleteTransferItems] = useDeleteTransferItemsMutation();
  const [fixMissingProductIds] = useFixMissingProductIdsMutation();

  // Normalize transfers
  const normalizedTransfers = useMemo(() => {
    return (
      transfersData?.transfers?.map((transfer) => ({
        ...transfer,
        transferItems: transfer.transferItems.map((item, index) => ({
          transferId: transfer._id,
          itemIndex: index,
          uniqueId: `${transfer._id}-${index}`,
          name: item.name,
          qty: item.qty,
          price: item.price,
          date: transfer.date,
        })),
      })) || []
    );
  }, [transfersData]);

  // Search
  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const matches = normalizedTransfers.flatMap((transfer) =>
      transfer.transferItems.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setPreviewResults(matches);
    setSelectedItems({});
    setSelectAll(false);
  };

  // Month filter
  const monthFilteredResults = useMemo(() => {
    if (!selectedMonth) return previewResults;
    return previewResults.filter((item) => {
      const d = new Date(item.date);
      return d.getMonth() + 1 === Number(selectedMonth);
    });
  }, [previewResults, selectedMonth]);

  // Monthly summary
  const monthlySummary = useMemo(() => {
    if (!selectedMonth) return null;

    const totalQty = monthFilteredResults.reduce((sum, item) => sum + item.qty, 0);
    const totalPrice = monthFilteredResults.reduce(
      (sum, item) => sum + item.price * item.qty,
      0
    );

    return { totalQty, totalPrice };
  }, [monthFilteredResults, selectedMonth]);

  // Select all
  const toggleSelectAll = () => {
    const newState = !selectAll;
    setSelectAll(newState);

    if (newState) {
      const all = {};
      monthFilteredResults.forEach((item) => {
        all[item.uniqueId] = true;
      });
      setSelectedItems(all);
    } else {
      setSelectedItems({});
    }
  };

  // Select single
  const toggleSelectItem = (uniqueId) => {
    setSelectedItems((prev) => {
      const updated = { ...prev };
      if (updated[uniqueId]) delete updated[uniqueId];
      else updated[uniqueId] = true;
      return updated;
    });
  };

  // Delete selected items
  const handleDelete = async () => {
    const selectedIds = Object.keys(selectedItems);
    if (selectedIds.length === 0) {
      toast.info('No items selected for deletion');
      return;
    }

    setIsDeleting(true);

    try {
      const groupedByTransfer = {};

      monthFilteredResults.forEach((item) => {
        if (selectedItems[item.uniqueId]) {
          if (!groupedByTransfer[item.transferId]) groupedByTransfer[item.transferId] = [];
          groupedByTransfer[item.transferId].push(item.itemIndex);
        }
      });

      const totalTransfersToUpdate = Object.keys(groupedByTransfer).length;
      let processed = 0;

      for (const transferId of Object.keys(groupedByTransfer)) {
        await deleteTransferItems({
          transferId,
          transferItemsIndexes: groupedByTransfer[transferId],
        }).unwrap();

        processed += 1;
        setProgress(Math.round((processed / totalTransfersToUpdate) * 100));
      }

      toast.success('Selected items deleted successfully');
      setPreviewResults([]);
      setSelectedItems({});
      setSelectAll(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed');
    } finally {
      setIsDeleting(false);
      setProgress(0);
    }
  };

  // Fix missing product IDs
  const handleFixMissingIds = async () => {
    try {
      await fixMissingProductIds().unwrap();
      toast.success('Missing product IDs repaired successfully');
    } catch (err) {
      toast.error(err?.data?.message || 'Fix failed');
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col md={3}>
          <h5 className="mb-3">Delete Items</h5>

          <Form.Select
            className="mb-3"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">Filter by Month</option>
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </Form.Select>

          <Button
            variant="secondary"
            disabled={!selectedMonth || monthFilteredResults.length === 0}
            onClick={() => {
              const all = {};
              monthFilteredResults.forEach((item) => {
                all[item.uniqueId] = true;
              });
              setSelectedItems(all);
              toast.info('All rows for this month selected');
            }}
            className="mb-3 w-100"
          >
            Select All for This Month
          </Button>

          <Button
            variant="outline-danger"
            disabled={!selectedMonth}
            onClick={() => setSelectedMonth('')}
            className="mb-3 w-100"
          >
            Clear Month Filter
          </Button>

          <Button
            variant="warning"
            onClick={handleFixMissingIds}
            className="mb-3 w-100"
          >
            Fix All Missing Product IDs
          </Button>

          {monthlySummary && (
            <ListGroup className="mt-4">
              <ListGroup.Item active>Monthly Summary</ListGroup.Item>
              <ListGroup.Item>Total Qty: {monthlySummary.totalQty}</ListGroup.Item>
              <ListGroup.Item>
                Total Price: D{monthlySummary.totalPrice.toFixed(2)}
              </ListGroup.Item>
            </ListGroup>
          )}
        </Col>

        <Col md={9}>
          <Row className="mb-3">
            <Col>
              <h1>Transfers Search & Delete</h1>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={8}>
              <Form.Control
                type="text"
                placeholder="Search for product name in transfers"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={!searchTerm.trim() || isDeleting}
              >
                Preview Matches
              </Button>
            </Col>
          </Row>

          {isDeleting && (
            <ProgressBar now={progress} label={`${progress}%`} className="mb-3" />
          )}

          {monthFilteredResults.length === 0 ? (
            <Alert variant="warning">No matching items found.</Alert>
          ) : (
            <>
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={selectAll}
                onChange={toggleSelectAll}
                className="mb-2"
              />

              <Table striped bordered hover responsive className="table-sm">
                <thead>
                  <tr>
                    <th></th>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Qty</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {monthFilteredResults.map((item) => (
                    <tr key={item.uniqueId}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={!!selectedItems[item.uniqueId]}
                          onChange={() => toggleSelectItem(item.uniqueId)}
                        />
                      </td>
                      <td>{new Date(item.date).toLocaleDateString()}</td>
                      <td>{item.name}</td>
                      <td>{item.qty}</td>
                      <td>D{item.price.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={isDeleting}
                className="mt-3"
              >
                Delete Selected Items
              </Button>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default TransfersSearchDeleteScreen;
